import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  MapPin, Clock, Loader2, ArrowLeft, Share2, Banknote, Users,
  Zap, Send
} from "lucide-react";
import { format } from "date-fns";
import ShareDialog from "@/components/ShareDialog";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

const ACTIVITY_COLORS = {
  basketball: "bg-orange-100 text-orange-700 border-orange-200",
  volleyball: "bg-yellow-100 text-yellow-700 border-yellow-200",
  badminton: "bg-green-100 text-green-700 border-green-200",
  basketball_3x3: "bg-amber-100 text-amber-700 border-amber-200",
  zumba: "bg-pink-100 text-pink-700 border-pink-200",
  taekwondo: "bg-red-100 text-red-700 border-red-200",
  dance: "bg-violet-100 text-violet-700 border-violet-200",
  yoga: "bg-purple-100 text-purple-700 border-purple-200",
  boxing: "bg-rose-100 text-rose-700 border-rose-200",
  martial_arts: "bg-amber-100 text-amber-700 border-amber-200",
  community_event: "bg-blue-100 text-blue-700 border-blue-200",
  aerobics: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  pickleball: "bg-cyan-100 text-cyan-700 border-cyan-200",
  tennis: "bg-lime-100 text-lime-700 border-lime-200",
  football: "bg-emerald-100 text-emerald-700 border-emerald-200",
  table_tennis: "bg-teal-100 text-teal-700 border-teal-200",
};

export default function MultiSportVenueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [requestNotes, setRequestNotes] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [venueRes, productsRes] = await Promise.all([
          api.get(`/space-engine/venues/${id}`),
          api.get(`/space-engine/venues/${id}/discovery-products`),
        ]);
        setVenue(venueRes.data);
        setProducts(productsRes.data);
        if (venueRes.data.spaces?.length) {
          setSelectedSpace(venueRes.data.spaces[0]);
        }
      } catch {
        toast.error("Failed to load venue");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const loadSlots = useCallback(async () => {
    if (!selectedSpace) return;
    setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await api.get(`/space-engine/spaces/${selectedSpace.physical_space_id}/availability`, {
        params: { date: dateStr }
      });
      setSlots(res.data.slots || []);
    } catch {
      toast.error("Failed to load availability");
    } finally {
      setSlotsLoading(false);
    }
  }, [selectedSpace, selectedDate]);

  useEffect(() => { if (selectedSpace) loadSlots(); }, [loadSlots, selectedSpace]);

  const kycMessage = user ? getBookingKycMessage(user) : null;
  const kycOk = user ? hasApprovedBookingKyc(user) : false;

  const handleBook = async () => {
    if (!selectedSlot || !user) return;
    setBookingLoading(true);
    try {
      if (selectedSlot.booking_mode === "request_only") {
        await api.post("/space-engine/booking-requests", {
          physical_space_id: selectedSpace.physical_space_id,
          activity_configuration_id: selectedSlot.activity_configuration_id,
          requested_start_at: selectedSlot.start,
          requested_end_at: selectedSlot.end,
          request_type: selectedSlot.partition_type === "full" ? "standard" : "partial_space",
          quoted_rate_amount: selectedSlot.rate,
          notes_from_requester: requestNotes,
        });
        toast.success("Booking request submitted! The venue owner will review it.");
      } else {
        await api.post("/space-engine/book", {
          physical_space_id: selectedSpace.physical_space_id,
          activity_configuration_id: selectedSlot.activity_configuration_id,
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          payment_mode: "CASH",
        });
        toast.success("Booking confirmed!");
      }
      setBookingDialog(false);
      loadSlots();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!venue) return <div className="p-4 text-center">Venue not found</div>;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-6" data-testid="venue-discovery-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold" data-testid="venue-name">{venue.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{venue.address || venue.city}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}><Share2 className="h-4 w-4" /></Button>
      </div>

      {kycMessage && <KycBookingNotice message={kycMessage} compact testId="venue-kyc-notice" />}

      {/* Discovery Products */}
      <div>
        <h2 className="text-lg font-semibold mb-3" data-testid="products-heading">Available Activities</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {products.map(p => (
            <Card key={p.discovery_product_id} className="cursor-pointer hover:border-primary/50 transition-colors" data-testid={`product-card-${p.discovery_product_id}`}>
              <CardContent className="p-4">
                <p className="font-medium text-sm">{p.product_name}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Badge variant={p.product_type === "instant_book" ? "default" : p.product_type === "request_only" ? "secondary" : "outline"} className="text-xs">
                    {p.product_type === "instant_book" ? "Instant Book" : p.product_type === "request_only" ? "Request Only" : "Program"}
                  </Badge>
                  {p.display_from_price_amount && <Badge variant="outline" className="text-xs">From PHP {p.display_from_price_amount}</Badge>}
                  {p.is_non_peak_only && <Badge variant="outline" className="text-xs">Off-Peak Only</Badge>}
                  <Badge className={`text-xs ${ACTIVITY_COLORS[p.marketing_tags_json?.activity?.toLowerCase()?.replace(/ /g, "_")] || "bg-gray-100 text-gray-600"}`}>{p.activity_name || p.marketing_tags_json?.activity || ""}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{p.space_name} - {p.partition_name || ""}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {products.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No bookable activities available at this venue yet.</p>}
      </div>

      <Separator />

      {/* Space Selector + Calendar */}
      {venue.spaces?.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-4">
            <div>
              <Label className="text-sm font-medium">Select Space</Label>
              <Select value={selectedSpace?.physical_space_id || ""} onValueChange={v => {
                const sp = venue.spaces.find(s => s.physical_space_id === v);
                setSelectedSpace(sp);
              }}>
                <SelectTrigger data-testid="space-selector"><SelectValue /></SelectTrigger>
                <SelectContent>{venue.spaces.map(s => <SelectItem key={s.physical_space_id} value={s.physical_space_id}>{s.space_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Calendar mode="single" selected={selectedDate} onSelect={d => d && setSelectedDate(d)} className="border rounded-lg" data-testid="date-calendar" />
          </div>

          {/* Available Slots */}
          <div className="lg:col-span-2">
            <h3 className="text-base font-semibold mb-3" data-testid="slots-heading">
              Available Slots — {format(selectedDate, "EEE, MMM d, yyyy")}
              {slotsLoading && <Loader2 className="h-4 w-4 animate-spin inline ml-2" />}
            </h3>
            {slots.length === 0 && !slotsLoading && <p className="text-sm text-muted-foreground py-8 text-center">No available slots for this date.</p>}
            <div className="grid gap-2 sm:grid-cols-2">
              {slots.map((slot, i) => (
                <button key={i} disabled={!slot.available || !kycOk}
                  onClick={() => { setSelectedSlot(slot); setBookingDialog(true); setRequestNotes(""); }}
                  className={`text-left p-3 rounded-lg border transition-colors ${slot.available ? "hover:border-primary/50 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                  data-testid={`slot-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {slot.start?.slice(11, 16)} - {slot.end?.slice(11, 16)}
                    </span>
                    {slot.is_peak && <Badge variant="destructive" className="text-xs">Peak</Badge>}
                  </div>
                  <p className="text-xs mt-1">{slot.configuration_name || slot.activity_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <Badge className={`text-xs ${ACTIVITY_COLORS[slot.activity_type_id] || "bg-gray-100 text-gray-600"}`}>{slot.partition_name}</Badge>
                    <span className="text-sm font-semibold">{slot.currency} {slot.rate}</span>
                  </div>
                  {!slot.available && slot.reason && <p className="text-xs text-destructive mt-1">{slot.reason}</p>}
                  {slot.booking_mode === "request_only" && slot.available && <Badge variant="secondary" className="text-xs mt-1">Request Only</Badge>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedSlot?.booking_mode === "request_only" ? "Submit Booking Request" : "Confirm Booking"}</DialogTitle>
            <DialogDescription>
              {selectedSlot?.configuration_name || selectedSlot?.activity_name} — {selectedSlot?.start?.slice(11, 16)} to {selectedSlot?.end?.slice(11, 16)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Space</span><span className="font-medium">{selectedSpace?.space_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Partition</span><span className="font-medium">{selectedSlot?.partition_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Rate</span><span className="font-medium">{selectedSlot?.currency} {selectedSlot?.rate}/hr</span>
            </div>
            {selectedSlot?.is_peak && <div className="flex justify-between text-sm"><span>Period</span><Badge variant="destructive" className="text-xs">Peak Hour</Badge></div>}
            {selectedSlot?.booking_mode === "request_only" && (
              <div>
                <Label>Notes for Venue Owner</Label>
                <Input value={requestNotes} onChange={e => setRequestNotes(e.target.value)} placeholder="Optional notes about your booking request" data-testid="request-notes-input" />
              </div>
            )}
            <Separator />
            <Button onClick={handleBook} disabled={bookingLoading} className="w-full gap-2" data-testid="confirm-booking-btn">
              {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : selectedSlot?.booking_mode === "request_only" ? <Send className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
              {selectedSlot?.booking_mode === "request_only" ? "Submit Request" : "Book Now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} venueId={id} />
    </div>
  );
}
